<template>
  <slot
    name="any"
    :data="result?.data"
    :refresh="launchGettingFlow"
    :loading="loading"
    :result="result"
    :loadingError="loadingError"
  >
    <slot
      v-if="loadingError"
      :refresh="launchGettingFlow"
      :result="result"
      name="error"
    >
      <div class="flex flex-col items-center justify-center p-8 text-center">
        <div class="text-slate-800 text-lg font-bold mb-2">
          {{ ['deals', 'coupons'].includes(dataKey) ? 'De bonnes réductions vous attendent ici' : 'Information temporairement indisponible' }}
        </div>
        <div class="text-slate-500 text-sm mb-4">
          {{ ['deals', 'coupons'].includes(dataKey) ? 'Revenez bientôt pour profiter de réductions exclusives !' : (result?.message?.message || result?.message || 'Veuillez réessayer dans un instant') }}
        </div>
        <button
          @click="launchGettingFlow"
          class="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl shadow-md transition-colors"
        >
          Actualiser
        </button>
      </div>
    </slot>

    <slot
      v-else-if="!loading && emptyChecker && emptyChecker(result?.data)"
      :refresh="launchGettingFlow"
      :result="result"
      name="empty"
    >
      <div class="flex flex-col items-center justify-center p-8">
        <div class="text-gray-400 text-lg mb-2">No data available</div>
        <button
          @click="launchGettingFlow"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Refresh
        </button>
      </div>
    </slot>

    <slot
      v-else
      :data="result?.data"
      :refresh="launchGettingFlow"
      :loading="loading"
    >
      <div v-if="loading" class="p-8 text-center text-gray-500">Loading...</div>
      <div v-else>{{ result?.data }}</div>
    </slot>
  </slot>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  computed,
  getCurrentInstance
} from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const { proxy } = getCurrentInstance();
const { $stores } = proxy;

const props = defineProps({
  dataKey: {
    type: String,
    required: true
  },
  emptyChecker: {
    type: Function,
    default: null
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  refreshDelay: {
    type: Number,
    default: 0
  },
  refreshOnEvents: {
    type: [String, Array],
    default: () => []
  },
  debounceDelay: {
    type: Number,
    default: 1000
  },
  initDelay: {
    type: Number,
    default: 800
  },
  log: {
    type: Boolean,
    default: false
  },
  // New prop: use custom event system (like ui/core version)
  useCustomEvent: {
    type: Boolean,
    default: false
  },
  ignoreUserIdQuery: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'fetched',
  'beforeGetting',
  'startGetting',
  'loadingError'
]);

const result = ref({
  statusCode: 200,
  message: '',
  data: null,
  error: null
});

const loading = ref(true);
const loadingError = ref(false);
const isRefreshing = ref(false);

let refreshInterval = null;
let initTimeout = null;
let debounceTimeout = null;
let lastRequestId = '';
let lastResolvedRequest = '';

const eventHandlers = new Map();

const log = (...args) => {
  if (props.log) {
    console.log('[DataFetcher]', ...args);
  }
};

const fetchData = async ({ silent = false, reqId = null } = {}) => {
  if (!silent) {
    loading.value = true;
    loadingError.value = false;
    emit('startGetting');
  }

  const requestId = reqId || `${Date.now()}`;
  lastRequestId = requestId;

  log('fetchData ~ key:', props.dataKey);

  const perfStart = Date.now();
  let dataResult;

  try {
    if (props.useCustomEvent) {
      // Use custom event system (from ui/core version)
      await fetchWithCustomEvent(requestId, silent);
    } else {
      // Use network request system (from features/data version)
      dataResult = await makeNetworkRequest(requestId);

      if (lastRequestId !== requestId) return;

      if (!dataResult) {
        throw new Error('Error while getting data');
      }

      const hasError = dataResult.statusCode !== 200 || !dataResult.data;

      if (hasError && silent) return;

      result.value = {
        statusCode: dataResult.statusCode,
        message: dataResult.message,
        data: dataResult.data,
        error: dataResult.error
      };

      lastResolvedRequest = requestId;

      if (hasError) {
        throw new Error('Bad response from server');
      }

      emitFetched();
    }
  } catch (error) {
    if (silent) return;

    log('fetchData ~ error:', error, dataResult);

    if (!dataResult) {
      result.value = {
        statusCode: 500,
        message: error.message,
        data: null,
        error: error
      };
    }

    loadingError.value = true;
    emitFetched();
    emit('loadingError');
  }

  loading.value = false;

  log('fetchData ~ perf:', props.dataKey, `${Date.now() - perfStart}ms`);
};

// Custom event system (from ui/core version)
const fetchWithCustomEvent = (requestId, silent) => {
  return new Promise((resolve, reject) => {
    const eventName = `data:${props.dataKey}:fetch`;

    const timeout = setTimeout(() => {
      if (lastRequestId !== requestId) return;
      loading.value = false;
      loadingError.value = true;
      result.value = { error: new Error('Request timeout'), statusCode: 408 };
      emit('loadingError', result.value.error);
      reject(new Error('Request timeout'));
    }, 30000); // 30s timeout

    bus.emit(eventName, {
      filters: props.filters,
      onSuccess: (data) => {
        clearTimeout(timeout);
        if (lastRequestId !== requestId) return;

        result.value = { data, statusCode: 200 };
        loading.value = false;
        loadingError.value = false;

        emitFetched();
        resolve(data);
      },
      onError: (error) => {
        clearTimeout(timeout);
        if (lastRequestId !== requestId) return;

        result.value = { error, statusCode: 500 };
        loading.value = false;
        loadingError.value = true;

        emit('loadingError', error);
        reject(error);
      }
    });
  });
};

const makeNetworkRequest = (requestId) => {
  return new Promise((resolve) => {
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success) {
          const transformedData = transformResponseData(
            props.dataKey,
            res.data
          );
          resolve({
            statusCode: 200,
            message: 'Success',
            data: transformedData,
            error: null
          });
        } else {
          resolve({
            statusCode: res.data?.statusCode || 500,
            message: res.data?.error || 'Network error',
            data: null,
            error: res.data?.error
          });
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

    const url = buildEndpointUrl(props.dataKey, props.filters);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: url,
      method: 'GET',
      data: props.filters
    });
  });
};

const transformResponseData = (key, rawData) => {
  if (!rawData) return null;

  // Handle different response formats based on the API structure
  if (rawData.data !== undefined) {
    const data = rawData.data;

    // Check if data has a key matching the dataKey (e.g., packages, orders)
    // and extract the array along with pagination info
    if (data[key] !== undefined && Array.isArray(data[key])) {
      return {
        result: data[key],
        total: data.total || data[key].length
      };
    }

    // If data is already an array, return it
    if (Array.isArray(data?.data)) {
      return {
        result: data.data,
        total: data.total || data.data.length
      };
    }

    // If data is already an array, return it
    if (Array.isArray(data)) {
      return data;
    }

    // Otherwise return data as-is
    return data;
  }

  if (rawData.appInfo) {
    // v2 API format: rawData.appInfo[entityName]
    const entityKey = key.charAt(0).toLowerCase() + key.slice(1);
    if (rawData.appInfo[entityKey]) {
      return rawData.appInfo[entityKey];
    }
    if (rawData.appInfo[key]) {
      return rawData.appInfo[key];
    }
  }

  // If the response is already an array, return it directly
  if (Array.isArray(rawData)) {
    return rawData;
  }

  // Default: return the raw data
  return rawData;
};

const buildEndpointUrl = (key, params) => {
  // Add userId to all requests (if not already present)
  const updatedParams = { ...params };
  if (
    !updatedParams.userId &&
    !props.useCustomEvent &&
    !props.ignoreUserIdQuery
  ) {
    updatedParams.userId = getUserId();
  }
  updatedParams.group = 'ultraxeno';

  const queryParams = new URLSearchParams(updatedParams).toString();
  const baseURL = constants.ENDPOINTS.CORE?.BASE || 'http://localhost:3000';

  // Map data keys to endpoint paths
  const endpointPaths = {
    dishes: '/rapidoo-aggregation/products-with-sides?isPublished=true',
    categories: '/categories/list',
    restaurants: '/restaurants/list',
    orders: (userId) => `/order/list?userId=${userId}`,
    deals: '/rapidoo-aggregation/deals?isPublished=true&onlyValidForNow=true',
    wallet: '/wallet',
    coupons: (userId) => `/coupons/list?userId=${userId}`,
    packages: '/packages',
    'food-delivery-fees': '/food-delivery-fees',
    notifications: '/notifications',
    transactions: '/transactions'
  };

  // Handle dynamic paths (like orders that need userId)
  let path;
  if (typeof endpointPaths[key] === 'function') {
    path = endpointPaths[key](getUserId());
  } else {
    path = endpointPaths[key] || `/api/${key}`;
  }
  const baseUrl = `${baseURL}${path}`;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return queryParams ? `${baseUrl}${separator}${queryParams}` : baseUrl;
};

const getUserId = () => {
  // Try to get userId from authenticated user store first
  const authenticatedUserId = $stores.user?.state?.current?.id;
  console.log('🚀 ~ getUserId ~ $stores.user?.state:', $stores.user?.state);
  console.log('🚀 ~ getUserId ~ authenticatedUserId:', authenticatedUserId);

  if (authenticatedUserId) {
    return authenticatedUserId;
  }

  // Fallback to localStorage for backward compatibility
  return localStorage.getItem('userId') || 'guest';
};

const emitFetched = () => {
  emit('fetched', {
    data: result.value.data,
    result: result.value,
    error: null
  });
};

const isLoading = () => {
  return lastResolvedRequest !== lastRequestId;
};

const debounceGetting = ({ reqId } = {}) => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  debounceTimeout = setTimeout(() => {
    fetchData({ reqId });
  }, props.debounceDelay);
};

const launchGettingFlow = () => {
  log('launchGettingFlow ~', props.dataKey);
  emit('beforeGetting');
  loading.value = true;
  loadingError.value = false;
  debounceGetting({ reqId: `${Date.now()}` });
};

const refresh = () => {
  launchGettingFlow();
};

const silentRefresh = () => {
  isRefreshing.value = true;
  fetchData({ silent: true }).finally(() => {
    isRefreshing.value = false;
  });
};

const initAutoRefresh = () => {
  if (props.refreshDelay > 0) {
    refreshInterval = setInterval(() => {
      if (!loadingError.value && !loading.value && !isRefreshing.value) {
        silentRefresh();
      }
    }, props.refreshDelay);
  }
};

const setupEventListeners = () => {
  const events = Array.isArray(props.refreshOnEvents)
    ? props.refreshOnEvents
    : props.refreshOnEvents
      ? [props.refreshOnEvents]
      : [];

  // Always listen to APPLICATION_WORK_FINISHED event like in v2
  const allEvents = ['APPLICATION_WORK_FINISHED', ...events];

  allEvents.forEach((eventName) => {
    const handler = () => {
      log('Event triggered refresh:', eventName);
      silentRefresh();
    };

    bus.on(eventName, handler);
    eventHandlers.set(eventName, handler);
  });
};

const cleanupEventListeners = () => {
  eventHandlers.forEach((handler, eventName) => {
    bus.off(eventName, handler);
  });
  eventHandlers.clear();
};

const init = () => {
  fetchData();
  initAutoRefresh();
  setupEventListeners();
};

// Watchers
watch(
  () => props.refreshOnEvents,
  (newVal) => {
    log('props updated refreshOnEvents', newVal);
    cleanupEventListeners();
    setupEventListeners();
    launchGettingFlow();
  },
  { deep: true }
);

watch(
  () => props.filters,
  (newVal, oldVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      log('props updated filters', newVal);
      launchGettingFlow();
    }
  },
  { deep: true }
);

// Lifecycle
onMounted(() => {
  initTimeout = setTimeout(() => {
    init();
  }, props.initDelay);
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
  if (initTimeout) clearTimeout(initTimeout);
  if (debounceTimeout) clearTimeout(debounceTimeout);
  cleanupEventListeners();
});

defineExpose({
  refresh,
  launchGettingFlow,
  fetchData,
  isLoading
});
</script>
